import { useRef, useCallback, useEffect } from 'react';

import { Dealer, DealersList } from '../typings/main';

interface UseMapMarkersProps {
  dealers: DealersList[];
  map: google.maps.Map | null;
  selectedIcon: string;
  selectedIndex: number;
  unselectedIcon: string;
  userPreferredDealer?: Dealer;

  onMarkerClick?: (dealer: DealersList, index: number) => void;
}

export const useMapMarkers = ({
  dealers,
  map,
  onMarkerClick,
  selectedIcon,
  selectedIndex,
  unselectedIcon,
  userPreferredDealer,
}: UseMapMarkersProps) => {
  const hasInitializedBounds = useRef<boolean>(false);
  const markersRef = useRef<google.maps.Marker[]>([]);

  const getDealerName = (dealerNames: { name: string; nameTypeCode: string }[]): string => {
    return Array.isArray(dealerNames) ? dealerNames[0]?.name || '' : '';
  };

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
  }, []);

  const isPreferredDealer = useCallback((dealer: Dealer): boolean => {
    if (!userPreferredDealer || !dealer) return false;

    return dealer.id === userPreferredDealer.id
  }, [userPreferredDealer])

  const createMarkers = useCallback(() => {
    if (!map || dealers.length === 0) return;

    clearMarkers();

    dealers.forEach((dealer, index) => {
      const shouldShowSelectedIcon = userPreferredDealer && isPreferredDealer(dealer as unknown as Dealer);

      const marker = new google.maps.Marker({
        position: {
          lat: dealer.location.latitude,
          lng: dealer.location.longitude
        },
        map: map,
        title: getDealerName(dealer.dealerNames),
        zIndex: shouldShowSelectedIcon ? 1000 : index + 1, // ✅ Preferred dealer on top
        icon: shouldShowSelectedIcon ? selectedIcon : unselectedIcon,
      });

      if (onMarkerClick) {
        marker.addListener('click', () => {
          onMarkerClick(dealer, index);
        })
      }

      markersRef.current.push(marker);
    });

    // Fit bounds if multiple dealers
    if (dealers.length > 1 && !hasInitializedBounds.current) {
      const bounds = new google.maps.LatLngBounds();
      dealers.forEach(dealer => {
        bounds.extend({
          lat: dealer.location.latitude,
          lng: dealer.location.longitude
        });
      });
      map.fitBounds(bounds);
      hasInitializedBounds.current = true; // ✅ Mark as initialized
    }
  }, [
    clearMarkers,
    dealers,
    isPreferredDealer,
    map,
    onMarkerClick,
    selectedIcon,
    unselectedIcon,
    userPreferredDealer,
  ]);

  // ✅ Update only marker icons when selectedIndex changes (not recreate all markers)
  useEffect(() => {
    if (!map || dealers.length === 0) return;

    markersRef.current.forEach((marker, index) => {
      const dealer = dealers[index];
      const shouldShowSelectedIcon = userPreferredDealer && isPreferredDealer(dealer as unknown as Dealer);

      marker.setIcon(shouldShowSelectedIcon ? selectedIcon : unselectedIcon);
      marker.setZIndex(shouldShowSelectedIcon ? 1000 : index + 1);
    });
  }, [
    dealers,
    isPreferredDealer,
    map,
    selectedIcon,
    selectedIndex,
    unselectedIcon,
    userPreferredDealer,
  ]);


  // Update markers when dependencies change
  useEffect(() => {
    createMarkers();
  }, [createMarkers]);

  // ✅ Reset bounds flag when dealers list changes
  useEffect(() => {
    hasInitializedBounds.current = false;
  }, [dealers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => clearMarkers();
  }, [clearMarkers]);

  return {
    createMarkers,
    clearMarkers
  };
};